import type { Socket } from 'phoenix';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { ACTION_TANDEM_ERROR, CATEGORY_PRODUCT } from '../constants/analytics';
import UIActions from '../ui/UIActions';
import { track } from '../utils/analytics';
import Logger from '../utils/Logger';
import { EditorActions } from '../editor/editorSlice';

export const Status = {
  SAVED: 'saved',
  SAVING: 'saving',
};

const MAX_RECONNECT_ATTEMPTS = 2;
const SUBJECTIVELY_OFFLINE_TIMEOUT = 30000;
const WARNING_TIMEOUT = 15000;

const LOCK_TOAST = {
  level: 'error',
  message:
    'Update rejected by server. Please save your most recent changes and refresh the page.',
};

const OFFLINE_TOAST = {
  level: 'error',
  message: 'Offline. Please save your work and reconnect.',
};

const SAVED_TOAST = { level: 'success', message: 'Saved!' };

const WARNING_TOAST = { level: 'warning', message: 'Still trying to save…' };

const PostSaveTracker = () => {
  const socket = useAppSelector(
    // @ts-expect-error
    (state) => (state.editor.channel?.socket ?? null) as Socket | null,
  );
  const postId = useAppSelector((state) => state.editor.postId);
  const isLocked = useAppSelector((state) => state.editor.locked);
  const hasUnsavedChanges = useAppSelector(
    (state) => state.editor.unsaved.length() > 0,
  );
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const saveStatus = useAppSelector((state) => state.editor.saveStatus);

  const reconnectAttemptsRef = useRef<number>(0);

  const dispatch = useAppDispatch();

  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const subjectivelyOfflineTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [timerTriggered, setTimerTriggered] = useState<
    'error' | 'warning' | null
  >(null);

  useEffect(
    () => () => {
      if (warningTimerRef.current !== null) {
        clearTimeout(warningTimerRef.current);
      }
      if (subjectivelyOfflineTimerRef.current !== null) {
        clearTimeout(subjectivelyOfflineTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      // Most recent browsers show a generic message now
      const message = 'Your work has not been fully saved. Leave anyways?';
      if (!isLocked && hasUnsavedChanges) {
        event.returnValue = message; // eslint-disable-line no-param-reassign
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isLocked, hasUnsavedChanges]);

  const onLocked = useCallback(() => {
    if (timerTriggered !== 'error' && saveStatus === Status.SAVING) {
      dispatch(UIActions.pushToast(LOCK_TOAST, -1));
      if (warningTimerRef.current !== null) {
        clearTimeout(warningTimerRef.current);
      }
      if (subjectivelyOfflineTimerRef.current !== null) {
        clearTimeout(subjectivelyOfflineTimerRef.current);
      }
    }
  }, [dispatch, saveStatus, timerTriggered]);

  const onOffline = useCallback(() => {
    if (isLocked) {
      return;
    }
    setTimerTriggered('error');
    dispatch(UIActions.pushToast(OFFLINE_TOAST, -1));
    dispatch(EditorActions.lock());
    Logger.error('Post took too long to save', { postId });
    track(ACTION_TANDEM_ERROR, {
      category: CATEGORY_PRODUCT,
      label: 'offline',
    });
  }, [dispatch, isLocked, postId]);

  const onWarning = useCallback(() => {
    if (isLocked) {
      return;
    }
    setTimerTriggered('warning');
    dispatch(
      UIActions.pushToast(
        WARNING_TOAST,
        SUBJECTIVELY_OFFLINE_TIMEOUT * (MAX_RECONNECT_ATTEMPTS + 1) -
          WARNING_TIMEOUT -
          10,
      ),
    );

    Logger.warn('Post save tracker warning', { postId });
  }, [dispatch, isLocked, postId]);

  const onSubjectivelyOffline = useCallback(() => {
    const shouldRetry =
      reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS && socket;

    if (shouldRetry) {
      reconnectAttemptsRef.current += 1;
      socket.disconnect(() => {
        socket.connect();
      });

      subjectivelyOfflineTimerRef.current = setTimeout(
        onSubjectivelyOffline,
        SUBJECTIVELY_OFFLINE_TIMEOUT,
      );

      Logger.warn('Subjectively offline', {
        postId,
        attempts: reconnectAttemptsRef.current,
      });
    } else {
      onOffline();
    }
  }, [socket, onOffline, postId]);

  const updateStatus = useCallback(() => {
    const newStatus = hasUnsavedChanges ? Status.SAVING : Status.SAVED;
    if (newStatus === Status.SAVING || newStatus === Status.SAVED) {
      if (subjectivelyOfflineTimerRef.current !== null) {
        clearTimeout(subjectivelyOfflineTimerRef.current);
      }
      if (warningTimerRef.current !== null) {
        clearTimeout(warningTimerRef.current);
      }
    }
    if (newStatus === Status.SAVING) {
      // Skip showing a warning if it is already being handled
      if (timerTriggered !== 'warning') {
        warningTimerRef.current = setTimeout(onWarning, WARNING_TIMEOUT);
      }
      subjectivelyOfflineTimerRef.current = setTimeout(
        onSubjectivelyOffline,
        SUBJECTIVELY_OFFLINE_TIMEOUT,
      );
    } else if (newStatus === Status.SAVED && timerTriggered) {
      reconnectAttemptsRef.current = 0;
      dispatch(UIActions.clearToasts());
      dispatch(UIActions.pushToast(SAVED_TOAST, 750));
      setTimerTriggered(null);
    }
    const dispatchStatus = readOnly ? '' : newStatus;
    if (saveStatus !== dispatchStatus) {
      dispatch(EditorActions.setSaveStatus(dispatchStatus));
    }
  }, [
    dispatch,
    onSubjectivelyOffline,
    onWarning,
    readOnly,
    saveStatus,
    hasUnsavedChanges,
    timerTriggered,
  ]);

  const { callback: debouncedUpdateStatus } = useDebouncedCallback(
    updateStatus,
    500,
    {
      maxWait: WARNING_TIMEOUT / 2,
    },
  );

  useEffect(() => {
    if (isLocked) {
      onLocked();
    } else if (saveStatus === Status.SAVING) {
      debouncedUpdateStatus(); // Prevent typing flicker
    } else {
      updateStatus();
    }
  }, [
    postId,
    saveStatus,
    isLocked,
    hasUnsavedChanges,
    readOnly,
    onLocked,
    debouncedUpdateStatus,
    updateStatus,
  ]);

  return null;
};

export default PostSaveTracker;
